/* ============================================================
   Collection Quick Add — collection-quick-add.js
   Frictionless "Add to Cart" on product grid cards.

   - Single-variant product  → adds to cart instantly.
   - Multi-variant product   → opens an inline variant panel
                               ON the card (no modal), then adds.
   On success: dispatches cart:build + cart:open so the rebuilt
   cart drawer (snippets/cart-drawer.liquid) shows the new item.

   Pattern adapted from assets/bundle-picker.js (openInlineVariants).
   ============================================================ */

(function () {
  'use strict';

  /* The pill lives inside the product card <a>, and the theme's
     quick-shop preloads a (now-removed) modal on hover. Neutralize
     that orphaned preload so it stops fetching ?view=modal. */
  function neutralizeThemePreload() {
    if (window.theme && typeof theme.preloadProductModal === 'function') {
      theme.preloadProductModal = function () {};
    }
  }

  var ADD_URL = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

  /* ── Normalize Shopify image URLs (strip size suffix, add https) ── */
  function normalizeImgSrc(src) {
    if (!src) return '';
    var clean = String(src).replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*)(?=\.[a-z]{3,4}(\?|$))/i, '');
    if (clean.startsWith('//')) clean = 'https:' + clean;
    return clean;
  }

  function money(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  /* ── State: only one inline panel open at a time ── */
  var activeCard = null;

  function closePanel(card) {
    if (!card) return;
    var panel = card.querySelector('.av-qa-panel');
    if (panel) panel.remove();
    card.classList.remove('av-qa-open');
    if (activeCard === card) activeCard = null;
  }

  function closeAllPanels() {
    document.querySelectorAll('.av-qa-panel').forEach(function (p) {
      var c = p.closest('.grid-product');
      if (c) c.classList.remove('av-qa-open');
      p.remove();
    });
    activeCard = null;
  }

  /* ── Add a variant to the cart, then open the drawer ── */
  function addToCart(variantId, btn, card) {
    var originalHTML = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function () {
        // Restore the button (the pill persists after a single-variant add;
        // the panel's Add button is removed by closePanel below).
        if (btn) { btn.disabled = false; btn.innerHTML = originalHTML; }
        if (card) closePanel(card);
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.innerHTML = originalHTML; }
        var pill = card && card.querySelector('.av-quick-add');
        if (pill) { pill.classList.add('av-qa-error'); setTimeout(function () { pill.classList.remove('av-qa-error'); }, 1500); }
      });
  }

  /* ── Does the product need a variant choice? ── */
  function hasRealOptions(product) {
    return product.options.length > 0 &&
      !(product.options.length === 1 && product.options[0].name === 'Title') &&
      !(product.variants.length === 1);
  }

  /* ── Build & open the inline variant panel on a card ── */
  function openPanel(card, handle, pill) {
    if (activeCard === card) { closePanel(card); return; }
    closeAllPanels();
    activeCard = card;
    card.classList.add('av-qa-open');

    var mask = card.querySelector('.grid-product__image-mask') || card;

    var panel = document.createElement('div');
    panel.className = 'av-qa-panel';
    panel.innerHTML = '<p class="av-qa-loading">Loading…</p>';
    // Panel is nested inside the card <a>. Child button handlers fire first
    // (bubbling), then this stops the click reaching the anchor (no nav) and
    // keeps the outside-click closer from firing.
    panel.addEventListener('click', function (e) {
      e.stopPropagation();
      e.preventDefault();
    });
    mask.appendChild(panel);

    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { if (!res.ok) throw new Error(); return res.json(); })
      .then(function (product) {
        // Single variant — add instantly, no panel.
        if (!hasRealOptions(product)) {
          closePanel(card);
          addToCart(product.variants[0].id, pill, card);
          return;
        }
        renderPanel(panel, card, product);
      })
      .catch(function () {
        panel.innerHTML = '<p class="av-qa-loading">Could not load options.</p>';
      });
  }

  /* ── Helpers for color swatches + availability ── */
  function handleize(s) {
    return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function isColorOption(option) {
    var n = (option.name || '').toLowerCase();
    return n.indexOf('color') > -1 || n.indexOf('colour') > -1;
  }

  // Reuse the theme's own swatch (uploaded image + fallback color) already in
  // the card DOM (.color-swatch--<handle>), so swatches match the product page.
  function getSwatchStyle(card, val) {
    var el = card.querySelector('.color-swatch--' + handleize(val));
    if (!el) {
      el = Array.prototype.slice.call(card.querySelectorAll('.color-swatch')).filter(function (s) {
        return (s.getAttribute('aria-label') || '').toLowerCase().indexOf(val.toLowerCase()) > -1;
      })[0];
    }
    var out = { color: '', image: '' };
    if (el) { out.color = el.style.backgroundColor || ''; out.image = el.style.backgroundImage || ''; }
    if (!out.color) out.color = val.split(/\s+/).pop().toLowerCase(); // theme's fallback
    return out;
  }

  // Is there an available variant with option[optIdx]=val that also matches the
  // other already-selected options? Drives the greyed-out / sold-out state.
  function valueAvailable(product, optIdx, val, selected) {
    return product.variants.some(function (v) {
      if (v['option' + (optIdx + 1)] !== val) return false;
      if (v.available === false) return false;
      return product.options.every(function (opt, j) {
        if (j === optIdx) return true;
        var sel = selected[j];
        if (sel == null) return true;
        return v['option' + (j + 1)] === sel;
      });
    });
  }

  function renderPanel(panel, card, product) {
    panel.innerHTML = '';
    var selected = {};
    var variant = null;
    var optionButtons = {};

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'av-qa-close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&#x2715;';
    close.addEventListener('click', function () { closePanel(card); });
    panel.appendChild(close);

    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'av-qa-add';
    addBtn.textContent = 'Select an option';
    addBtn.disabled = true;

    // Option groups live in a scrollable body so the Add button stays pinned
    // and the panel can cap its height when there are many variants.
    var body = document.createElement('div');
    body.className = 'av-qa-body';

    // Grey out (and skip) any value that has no available variant given the
    // other current selections; deselect a value that becomes invalid.
    function updateAvailability() {
      Object.keys(optionButtons).forEach(function (idxStr) {
        var idx = parseInt(idxStr, 10);
        optionButtons[idx].forEach(function (o) {
          var ok = valueAvailable(product, idx, o.val, selected);
          o.btn.classList.toggle('av-qa-option-btn--soldout', !ok);
          o.btn.disabled = !ok;
          if (!ok && o.btn.classList.contains('active')) {
            o.btn.classList.remove('active');
            delete selected[idx];
          }
        });
      });
    }

    product.options.forEach(function (option, idx) {
      var swatches = isColorOption(option);
      var group = document.createElement('div');
      group.className = 'av-qa-option-group';

      var label = document.createElement('div');
      label.className = 'av-qa-option-label';
      label.textContent = option.name;

      var values = document.createElement('div');
      values.className = 'av-qa-option-values' + (swatches ? ' av-qa-option-values--swatch' : '');

      optionButtons[idx] = [];

      Array.from(new Set(option.values)).forEach(function (val) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', val);
        if (swatches) {
          b.className = 'av-qa-swatch';
          b.title = val;
          var st = getSwatchStyle(card, val);
          if (st.color) b.style.backgroundColor = st.color;
          if (st.image) b.style.backgroundImage = st.image;
        } else {
          b.className = 'av-qa-option-btn';
          b.textContent = val;
        }
        b.addEventListener('click', function () {
          if (b.disabled || b.classList.contains('av-qa-option-btn--soldout')) return;
          optionButtons[idx].forEach(function (o) { o.btn.classList.remove('active'); });
          b.classList.add('active');
          selected[idx] = val;
          updateAvailability();
          variant = resolve(product, selected, addBtn, card);
        });
        optionButtons[idx].push({ val: val, btn: b });
        values.appendChild(b);
      });

      group.appendChild(label);
      group.appendChild(values);
      body.appendChild(group);
    });

    updateAvailability(); // initial pass: grey out fully sold-out values

    addBtn.addEventListener('click', function () {
      if (!variant || !variant.available) return;
      addToCart(variant.id, addBtn, card);
    });

    panel.appendChild(body);
    panel.appendChild(addBtn);
  }

  /* ── Resolve the variant from the chosen options, update UI ── */
  function resolve(product, selected, addBtn, card) {
    var variant = product.variants.find(function (v) {
      return product.options.every(function (opt, idx) {
        var sel = selected[idx];
        if (!sel) return true;
        return v['option' + (idx + 1)] === sel;
      });
    }) || null;

    // Live card image swap (opacity fade — theme hijacks transform).
    if (variant) {
      var newSrc = null;
      if (variant.featured_image && variant.featured_image.src) {
        newSrc = normalizeImgSrc(variant.featured_image.src);
      } else if (product.images && product.images[0]) {
        newSrc = normalizeImgSrc(product.images[0].src || product.images[0]);
      }
      if (newSrc) {
        var img = card.querySelector('.grid-product__image, .grid__image-ratio img, .image-wrap img');
        if (img) {
          img.style.transition = 'opacity 0.2s ease';
          img.style.opacity = '0';
          setTimeout(function () {
            img.removeAttribute('srcset');
            img.src = newSrc;
            img.style.opacity = '1';
          }, 150);
        }
      }
    }

    var allChosen = Object.keys(selected).length === product.options.length;

    // Require every option chosen before enabling add (a partial match would
    // resolve to an arbitrary variant, e.g. an unpicked length).
    if (!allChosen) {
      addBtn.disabled = true;
      addBtn.classList.remove('av-qa-add--oos');
      addBtn.textContent = 'Select an option';
      return null;
    }

    if (!variant) {
      addBtn.disabled = true;
      addBtn.classList.add('av-qa-add--oos');
      addBtn.textContent = 'Unavailable';
      return null;
    }
    if (!variant.available) {
      addBtn.disabled = true;
      addBtn.classList.add('av-qa-add--oos');
      addBtn.textContent = 'Sold Out';
      return variant;
    }
    addBtn.disabled = false;
    addBtn.classList.remove('av-qa-add--oos');
    addBtn.textContent = 'Add to Cart';
    return variant;
  }

  /* ── Delegated click handling (survives grid reloads) ──
     Capture phase: the pill sits inside the card <a>, and the theme stops
     click propagation on the card before it reaches a bubble listener — so we
     must intercept on the way DOWN to cancel navigation and open the panel. */
  document.addEventListener('click', function (e) {
    var pill = e.target.closest('.av-quick-add');
    if (pill) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var card = pill.closest('.grid-product');
      var handle = pill.dataset.handle;
      if (card && handle) openPanel(card, handle, pill);
      return;
    }

    // Click outside an open panel (and not on a pill) closes it. Clicks INSIDE
    // the panel are left alone here so the option/add/close handlers run; the
    // panel's own bubble handler cancels the card-link navigation for those.
    if (activeCard && !e.target.closest('.av-qa-panel')) {
      closeAllPanels();
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllPanels();
  });

  function boot() { neutralizeThemePreload(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
